/**
 * OpenLayers-Stile je Ebene (§7.3 des Plans) — Markenpalette, plus die
 * Donut-Icon-Stile je Feature (§7.4).
 *
 * Jede Layer-Style-Funktion gibt zwei Styles zurück: [Polygonstil, Donut-Icon]
 * — der Donut sitzt via `geometry`-Override garantiert im Innenpunkt des
 * Polygons.
 */

import Feature from 'ol/Feature';
import { Geometry, Point, Polygon, MultiPolygon } from 'ol/geom';
import { Circle as CircleStyle, Fill, Icon, Stroke, Style } from 'ol/style';

import {
  dominanteKlasse,
  KlassenKey,
  KLASSEN,
  normiereVerteilung,
  verteilungSumme,
  Verteilung,
} from './klassen';
import { makeDonutCanvas } from './donut';

type Level = 'bundesland' | 'landkreis' | 'feld';

// --- Kreis-Größen je Ebene (BL/LK groß mit Labels, Feld klein) ---
export const DONUT_SIZE: Record<Level, number> = {
  bundesland: 84,
  landkreis: 58,
  feld: 26,
};

// Ring statt Vollkreis: Loch-Radius als Anteil des Außenradius (Kreis-/Donut-Plot).
const RING_LOCH = 0.55;

// Hover-Füllung in Markenblau (#0048a0) — einheitlich für alle Ebenen.
export const hoverStyle = new Style({
  stroke: new Stroke({ color: '#0048a0', width: 1.5 }),
  fill: new Fill({ color: 'rgba(0,72,160,0.45)' }),
  zIndex: 50,
});

/** Hover-Stil (alle Ebenen: Bundesland, Landkreis, Feld → Markenblau). */
export function hoverStyleFn(_feature: Feature<Geometry>): Style {
  return hoverStyle;
}

// Auswahl-Hervorhebung: dicke Markenblau-Kontur, KEINE Füllung — so bleibt die
// klassenbasierte (transparente) Einfärbung des Polygons sichtbar und der Donut
// wird nicht verdeckt. Konturbreite je Ebene: der gewählte Landkreis bekommt
// eine deutlich dickere Kontur (unabhängig davon, ob per Menü oder per Klick
// gewählt — beide Wege laufen über dieselbe Auswahl/Highlight-Ebene).
const HIGHLIGHT_STROKE_WIDTH: Record<Level, number> = {
  bundesland: 3.5,
  landkreis: 9,
  feld: 3.5,
};

// Highlight-Stile je Ebene (Konturbreite) → wenige, wiederverwendete Objekte.
const highlightStyleCache = new Map<Level, Style>();

function highlightStyleFor(level: Level): Style {
  const cached = highlightStyleCache.get(level);
  if (cached) return cached;
  const style = new Style({
    stroke: new Stroke({ color: '#0048a0', width: HIGHLIGHT_STROKE_WIDTH[level] }),
    fill: new Fill({ color: 'rgba(0,0,0,0)' }),
    zIndex: 100,
  });
  highlightStyleCache.set(level, style);
  return style;
}

// Rückwärtskompatibler Standard-Highlight-Stil (Landkreis-Breite).
export const highlightStyle = highlightStyleFor('landkreis');

// --- Polygon-Füllung nach dominanter Klasse (halbtransparent über der Basiskarte) ---

function hexToRgba(hex: string, alpha: number): string {
  const [r, g, b] = hexZuRgb(hex);
  return `rgba(${r},${g},${b},${alpha})`;
}

function hexZuRgb(hex: string): [number, number, number] {
  const h = hex.replace('#', '');
  return [
    parseInt(h.substring(0, 2), 16),
    parseInt(h.substring(2, 4), 16),
    parseInt(h.substring(4, 6), 16),
  ];
}

// --- Farbmodus der Polygonfüllung ---
// 'gradient' (Standard): nuancierte Farbe entlang Grün→Gelb→Rot je nach
//   Verteilung. 'dominant' (Backup): Farbe der dominanten Klasse.
const POLYGON_FARBMODUS: 'gradient' | 'dominant' = 'gradient';

// Gewichtung der Klassen NUR für die FÜLLFARBE (Polygon-Tönung BL/LK/Feld +
// Feld-Kreis) — NICHT für Donut, Info-Karte oder Legende, die alle die
// ungewichteten Flächenanteile (verteilungHa) zeigen. „auffällig" und
// „kritisch" bekommen deutlich mehr Gewicht, damit schon ein kleiner Anteil
// problematischer Fläche die Füllfarbe klar Richtung Gelb/Rot zieht, statt von
// der (meist dominierenden) vitalen Fläche „übertönt" zu werden. Höher =
// stärkerer Zug; frei justierbar, ohne dass sich Statistik/Donut ändern.
const FUELLFARBE_GEWICHT: Record<KlassenKey, number> = {
  gut: 1,
  ok: 4, // auffällig
  schlecht: 9, // kritisch
};

/**
 * Position 0..1 der Verteilung entlang des Verlaufs: GEWICHTETER Mittelwert der
 * Klassen-Indizes (KLASSEN[0]=0 … KLASSEN[n-1]=1), gewichtet mit
 * FUELLFARBE_GEWICHT. Grün (gut) → Rot (schlecht). Reine Ein-Klassen-
 * Verteilungen bleiben am jeweiligen Endpunkt; jede Beimischung von auffällig/
 * kritisch verschiebt die Farbe stärker als beim ungewichteten Mittel.
 * null, wenn keine verwertbare Verteilung vorliegt.
 */
function verteilungPosition(verteilung: Verteilung | undefined): number | null {
  const norm = normiereVerteilung(verteilung);
  if (!norm) return null;
  const n = norm.length;
  if (n < 2) return 0;
  let zaehler = 0;
  let nenner = 0;
  norm.forEach((seg, i) => {
    const g = FUELLFARBE_GEWICHT[seg.key] ?? 1;
    zaehler += g * seg.anteil * (i / (n - 1));
    nenner += g * seg.anteil;
  });
  return nenner > 0 ? zaehler / nenner : 0;
}

/** Farbe entlang des Verlaufs über die KLASSEN-Farben (Stops bei i/(n-1)). */
function verlaufFarbe(t: number): [number, number, number] {
  const stops = KLASSEN.map((k) => hexZuRgb(k.farbe));
  const n = stops.length;
  if (n === 1) return stops[0];
  const x = Math.min(Math.max(t, 0), 1) * (n - 1);
  const i = Math.min(Math.floor(x), n - 2);
  const f = x - i;
  const a = stops[i];
  const b = stops[i + 1];
  return [
    Math.round(a[0] + (b[0] - a[0]) * f),
    Math.round(a[1] + (b[1] - a[1]) * f),
    Math.round(a[2] + (b[2] - a[2]) * f),
  ];
}

// Randstil + Füll-Deckkraft je Ebene.
const STROKE: Record<Level, Stroke> = {
  bundesland: new Stroke({ color: '#0048a0', width: 1.5 }),
  landkreis: new Stroke({ color: '#0048a0', width: 0.9 }),
  feld: new Stroke({ color: '#0048a0', width: 0.5 }),
};
// feld: 0.5 → halbtransparent, damit die darunterliegende Karte und die dünne
// Landkreis-Grenzlinie (bei Feld-Zoom) durchscheinen. BL/LK bleiben ebenfalls
// halbtransparent über der Karte.
const FILL_ALPHA: Record<Level, number> = { bundesland: 0.3, landkreis: 0.28, feld: 0.5 };

// Dünne LK-Grenzlinie ohne Füllung: bei Feld-Zoom gezeichnet, damit die
// Landkreis-Grenzen ab der LK-Zoomstufe und näher immer sichtbar bleiben.
export const landkreisLinieStyle = new Style({ stroke: STROKE.landkreis });

// Polygonstile werden nach (Ebene, dominanter Klasse) gecacht → wenige Objekte.
const polygonCache = new Map<string, Style>();

function polygonStyle(level: Level, verteilung: Verteilung | undefined): Style {
  if (POLYGON_FARBMODUS === 'gradient') {
    const t = verteilungPosition(verteilung);
    if (t == null) return transparenterStyle(level);
    const qt = Math.round(t * 40); // auf 40 Stufen quantisiert → kompakter Cache
    const key = `${level}|grad|${qt}`;
    const cached = polygonCache.get(key);
    if (cached) return cached;
    const [r, g, b] = verlaufFarbe(qt / 40);
    const style = new Style({
      stroke: STROKE[level],
      fill: new Fill({ color: `rgba(${r},${g},${b},${FILL_ALPHA[level]})` }),
    });
    polygonCache.set(key, style);
    return style;
  }

  // Backup: Farbe der dominanten Klasse.
  const dom = dominanteKlasse(verteilung);
  if (!dom) return transparenterStyle(level);
  const key = `${level}|${dom.key}`;
  const cached = polygonCache.get(key);
  if (cached) return cached;
  const style = new Style({
    stroke: STROKE[level],
    fill: new Fill({ color: hexToRgba(dom.farbe, FILL_ALPHA[level]) }),
  });
  polygonCache.set(key, style);
  return style;
}

/** Randstil ohne Füllung (keine Verteilungsdaten). */
function transparenterStyle(level: Level): Style {
  const key = `${level}|none`;
  const cached = polygonCache.get(key);
  if (cached) return cached;
  const style = new Style({
    stroke: STROKE[level],
    fill: new Fill({ color: 'rgba(0,0,0,0)' }),
  });
  polygonCache.set(key, style);
  return style;
}

/**
 * Verteilung für die Kartendarstellung — Hektar-Basis (`verteilungHa`), mit
 * Fallback auf die generische `verteilung`.
 *
 * `verteilung` und `verteilungHa` sind auf allen Ebenen flächenbasiert
 * (`verteilung` = Flächen-%, `verteilungHa` = Fläche in Hektar) und liefern
 * daher identische Anteile — der Fallback ist reine Robustheit, falls einmal
 * nur eines der beiden Felder vorliegt.
 */
function darstellungsVerteilung(feature: Feature<Geometry>): Verteilung | undefined {
  const ha = feature.get('verteilungHa') as Verteilung | undefined;
  if (ha && verteilungSumme(ha) > 0) return ha;
  return feature.get('verteilung') as Verteilung | undefined;
}

// --- Innenpunkt einer (Multi-)Polygon-Geometrie für die Kuchen-Platzierung ---

function innenPunkt(geom: Geometry | undefined): Point | undefined {
  if (geom instanceof Polygon) return geom.getInteriorPoint();
  if (geom instanceof MultiPolygon) {
    const pts = geom.getInteriorPoints();
    const coords = pts.getCoordinates();
    if (coords.length) return new Point(coords[0]);
  }
  return undefined;
}

/**
 * Baut den Kuchen-Icon-Style für ein Feature. `size` in CSS-Pixeln.
 * Gibt null zurück, wenn keine Verteilung vorliegt (Kuchen ausgelassen).
 */
function pieStyle(
  feature: Feature<Geometry>,
  size: number,
  zeigeLabels: boolean
): Style | null {
  const verteilung = darstellungsVerteilung(feature);
  const canvas = makeDonutCanvas(verteilung, { size, lochAnteil: RING_LOCH, zeigeLabels });
  if (!canvas) return null;
  const scale = canvas.width ? size / canvas.width : 1; // 1/dpr → CSS-Größe
  return new Style({
    geometry: (f) => innenPunkt((f as Feature<Geometry>).getGeometry()),
    image: new Icon({
      img: canvas,
      imgSize: [canvas.width, canvas.height],
      scale,
      anchor: [0.5, 0.5],
    }),
    zIndex: 10,
  });
}

// --- Feld-Kreis (Zwischenzoom: Feld sichtbar, aber noch kein Donut) ---
// Ein kleiner, gefüllter Kreis im Feld-Innenpunkt. Die Farbe folgt DERSELBEN
// gewichteten Klassen-Farbmischung wie die Landkreis-/Feld-Füllung
// (verteilungPosition → verlaufFarbe). Nur für Felder — nicht für LK/BL.
const KREIS_RADIUS = 7; // CSS-Pixel

// Kreis-Stile nach quantisierter Verlaufsposition cachen (wenige Objekte, auch
// bei tausenden Feldern je Frame). Der Innenpunkt kommt über die geometry-
// Funktion pro Feature, daher ist ein geteilter Style unbedenklich.
const kreisCache = new Map<number, Style>();

function kreisStyle(feature: Feature<Geometry>): Style | null {
  const verteilung = darstellungsVerteilung(feature);
  const t = verteilungPosition(verteilung);
  if (t == null) return null;
  const qt = Math.round(t * 40); // gleiche Quantisierung wie polygonStyle
  const cached = kreisCache.get(qt);
  if (cached) return cached;
  const [r, g, b] = verlaufFarbe(qt / 40);
  const style = new Style({
    geometry: (f) => innenPunkt((f as Feature<Geometry>).getGeometry()),
    image: new CircleStyle({
      radius: KREIS_RADIUS,
      fill: new Fill({ color: `rgb(${r},${g},${b})` }),
      stroke: new Stroke({ color: 'rgba(255,255,255,0.9)', width: 1.5 }),
    }),
    zIndex: 10,
  });
  kreisCache.set(qt, style);
  return style;
}

// --- Öffentliche Style-Funktionen je Ebene: [Polygon-Tönung, Kuchen] ---

export function bundeslandStyleFn(feature: Feature<Geometry>): Style[] {
  const verteilung = darstellungsVerteilung(feature);
  const styles: Style[] = [polygonStyle('bundesland', verteilung)];
  const pie = pieStyle(feature, DONUT_SIZE.bundesland, true);
  if (pie) styles.push(pie);
  return styles;
}

export function landkreisStyleFn(feature: Feature<Geometry>): Style[] {
  const verteilung = darstellungsVerteilung(feature);
  const styles: Style[] = [polygonStyle('landkreis', verteilung)];
  const pie = pieStyle(feature, DONUT_SIZE.landkreis, true);
  if (pie) styles.push(pie);
  return styles;
}

/** Feld-Markierung je Zoom: 'donut' (nah), 'kreis' (Zwischenzoom), 'kein'. */
export type FeldMarker = 'kein' | 'kreis' | 'donut';

export function feldStyleFn(feature: Feature<Geometry>, marker: FeldMarker = 'donut'): Style[] {
  const verteilung = darstellungsVerteilung(feature);
  const styles: Style[] = [polygonStyle('feld', verteilung)];
  if (marker === 'donut') {
    const pie = pieStyle(feature, DONUT_SIZE.feld, false);
    if (pie) styles.push(pie);
  } else if (marker === 'kreis') {
    const kreis = kreisStyle(feature);
    if (kreis) styles.push(kreis);
  }
  return styles;
}

/**
 * Stil der Auswahl-Hervorhebung (oberste Ebene): dicke Blau-Kontur PLUS der
 * Donut der gewählten Region. So liegt der Donut garantiert über jeder
 * Füllfarbe und bleibt nach dem Hineinzoomen (z. B. LK-Klick) sichtbar.
 *
 * `zeigeKontur=false` blendet die dicke Kontur aus (Donut bleibt, falls
 * `zeigeDonut`): so kann die LK-Auswahllinie beim Hineinzoomen (Feldinspektion)
 * verschwinden, während sie beim Herauszoomen wieder erscheint (s. MapView).
 */
export function highlightStyleFn(
  feature: Feature<Geometry>,
  zeigeDonut = true,
  zeigeKontur = true
): Style[] {
  const level = (feature.get('__level') as Level) || 'landkreis';
  const styles: Style[] = zeigeKontur ? [highlightStyleFor(level)] : [];
  if (zeigeDonut) {
    const pie = pieStyle(feature, DONUT_SIZE[level] ?? DONUT_SIZE.landkreis, level !== 'feld');
    if (pie) styles.push(pie);
  }
  return styles;
}
